import { useEffect, useMemo, useState } from 'react';
import { Input, Message, toaster } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import { useCreateWorkplanFlag } from '../hooks/useQueries';
import { formatHours } from '../utils/chartUtils';

// Server keys -> labels; the order is the order the chips render in.
export const FLAG_REASONS = [
  { value: 'too_many_hours', label: 'Too many hours' },
  { value: 'too_few_hours', label: 'Too few hours' },
  { value: 'wrong_activities', label: 'Wrong activities' },
  { value: 'other', label: 'Other' },
];

const LEAD_SOURCE_LABELS = {
  discipline: 'Discipline lead',
  activity: 'Activity lead',
  pic: 'Project PIC',
};

// The API helpers throw "HTTP 409: {"error":"..."}"; show only the server's sentence.
const errorText = (e) => {
  const raw = String(e?.message || e || '').replace(/^HTTP \d+:\s*/, '');
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.error) return String(parsed.error);
  } catch {
    // not JSON
  }
  return raw || 'The flag could not be sent.';
};

// The email goes out from the no-reply sender, so the default note is written as a
// neutral notification rather than a first-person letter. The email around it already
// says who flagged what; this only states the concern and asks for a review.
const REASON_SENTENCES = {
  too_many_hours: 'The planned hours look too high for this month.',
  too_few_hours: 'The planned hours look too low for this month.',
  wrong_activities: 'The hours may be planned against the wrong activities.',
  other: 'The planned hours for this month need a review.',
};

const buildDefaultMessage = ({ reason, employeeName, monthName, taskName, hours }) =>
  [
    `${employeeName} has ${formatHours(hours)} planned on ${taskName} in ${monthName}.`,
    REASON_SENTENCES[reason] || REASON_SENTENCES.other,
    `Please review the forecast and follow up with ${employeeName}.`,
  ].join(' ');

/**
 * Flag one project/discipline x month to the lead who owns its forecast.
 *
 * target: { userId, projectName, taskId, taskName, monthStart, monthName, hours,
 *           activities: [{ name, hours }], lead: { name, email, source } }
 */
export default function FlagHoursModal({ open, target, employeeName, onClose }) {
  const createFlag = useCreateWorkplanFlag();
  const [reason, setReason] = useState(FLAG_REASONS[0].value);
  const [message, setMessage] = useState('');

  // rsuite keeps the modal mounted, so each open starts from a fresh draft rather than
  // whatever the last flag said.
  const defaultMessageFor = (r) =>
    buildDefaultMessage({
      reason: r,
      employeeName: employeeName || 'This employee',
      monthName: target?.monthName,
      taskName: target?.taskName,
      hours: target?.hours,
    });

  useEffect(() => {
    if (!open || !target) return;
    setReason(FLAG_REASONS[0].value);
    setMessage(defaultMessageFor(FLAG_REASONS[0].value));
    createFlag.reset();
    // createFlag.reset is stable; the draft only depends on what is being flagged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target, employeeName]);

  // Changing the concern rewrites the note only while it is still the untouched default;
  // anything the person typed is kept.
  const pickReason = (r) => {
    if (message === defaultMessageFor(reason)) setMessage(defaultMessageFor(r));
    setReason(r);
  };

  const plannedActivities = useMemo(
    () => (Array.isArray(target?.activities) ? target.activities.filter((a) => Number(a.hours) !== 0) : []),
    [target]
  );

  const pending = createFlag.isPending;
  const lead = target?.lead ?? null;
  const canSend = Boolean(lead?.email) && !pending;

  const close = () => {
    if (pending) return;
    onClose?.();
  };

  const send = async () => {
    if (!target || !canSend) return;
    try {
      const result = await createFlag.mutateAsync({
        userId: target.userId,
        taskId: target.taskId,
        monthStart: target.monthStart,
        reason,
        message,
      });
      toaster.push(
        <Message type="success" showIcon closable>
          {`Email sent to ${result?.recipient?.name || lead.name}.`}
        </Message>,
        { placement: 'topEnd', duration: 3500 }
      );
      onClose?.();
    } catch {
      // Shown in the body from createFlag.error.
    }
  };

  return (
    <Modal open={open} onClose={close} size="sm" className="ppc-mwp-modal">
      <Modal.Header closeButton={!pending}>
        <Modal.Title>Flag hours to your lead</Modal.Title>
      </Modal.Header>

      {target ? (
        <Modal.Body>
          <div className="ppc-mwp-flag">
            <div className="ppc-mwp-flag__summary">
              <div className="ppc-mwp-flag__summary-head">
                <div className="ppc-mwp-flag__titles">
                  <span className="ppc-mwp-flag__project">{target.projectName}</span>
                  <span className="ppc-mwp-flag__task">{`${target.taskName} · ${target.monthName}`}</span>
                </div>
                <span className="ppc-mwp-flag__hours ppc-mwp-num">{formatHours(target.hours)}</span>
              </div>
              {plannedActivities.length > 0 ? (
                <ul className="ppc-mwp-flag__acts">
                  {plannedActivities.map((a) => (
                    <li key={a.name}>
                      <span>{a.name}</span>
                      <span className="ppc-mwp-num">{formatHours(a.hours)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="ppc-mwp-flag__none">No hours planned this month.</div>
              )}
            </div>

            <div className="ppc-mwp-flag__to">
              <span className="ppc-mwp-modal__label">To</span>
              <span className="ppc-mwp-flag__lead">{lead?.name}</span>
              {lead?.email ? <span className="ppc-mwp-flag__email">{lead.email}</span> : null}
              {lead?.source ? <span className="ppc-mwp-count">{LEAD_SOURCE_LABELS[lead.source] || lead.source}</span> : null}
            </div>

            {lead && !lead.email ? (
              <Message type="warning" showIcon>
                {`${lead.name} has no email address on file, so this flag can't be sent.`}
              </Message>
            ) : null}

            <div className="ppc-mwp-modal__label" id="ppc-mwp-flag-reason-label">What's the concern?</div>
            <div className="ppc-mwp-flag__chips" role="group" aria-labelledby="ppc-mwp-flag-reason-label">
              {FLAG_REASONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  className={`ppc-mwp-chip${reason === r.value ? ' ppc-mwp-chip--active' : ''}`}
                  aria-pressed={reason === r.value}
                  onClick={() => pickReason(r.value)}
                  disabled={pending}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <label className="ppc-mwp-modal__label" htmlFor="ppc-mwp-flag-message">Note to lead</label>
            <Input
              id="ppc-mwp-flag-message"
              as="textarea"
              rows={6}
              maxLength={4000}
              value={message}
              onChange={(v) => setMessage(v)}
              disabled={pending}
            />

            {createFlag.isError ? (
              <Message type="error" showIcon>
                {errorText(createFlag.error)}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
      ) : null}

      <Modal.Footer>
        <NsiButton onClick={close} disabled={pending}>
          Cancel
        </NsiButton>
        <NsiButton variant="primary" onClick={send} loading={pending} disabled={!canSend}>
          Send email
        </NsiButton>
      </Modal.Footer>
    </Modal>
  );
}
